/**
 * Prijsmodel per evenement (deel 4, hoofdstuk 3). Alle drie de knoppen zijn
 * los aan te zetten, ook als je er maar één gebruikt:
 *
 *  1. vast tarief per kraamset per dag
 *  2. doorbelasting per bon (vast bedrag)
 *  3. doorbelasting over de omzet (percentage)
 *
 * Zo is een tariefwijziging van de betaalpartij een instelling, geen
 * verbouwing.
 */

import {
  percentageVan,
  tariefMaalAantal,
  vermenigvuldig,
  type Basispunten,
  type Cent,
  type TiendeCent,
} from './geld.js';

export interface Prijsmodel {
  /** Wat de organisator per kraamset per dag betaalt. */
  dagtariefPerKraamsetCent: Cent;
  /**
   * Doorbelaste transactiekosten als vast bedrag per bon, in tienden van een
   * cent. Banktarieven hebben drie decimalen: € 0,066 wordt 66.
   */
  perBonTiendeCent: TiendeCent;
  /** Doorbelaste transactiekosten als percentage over de omzet. */
  overOmzetBasispunten: Basispunten;
}

export const LEEG_PRIJSMODEL: Prijsmodel = {
  dagtariefPerKraamsetCent: 0,
  perBonTiendeCent: 0,
  overOmzetBasispunten: 0,
};

export interface TransactiekostenInvoer {
  aantalBonnen: number;
  /** Omzet exclusief statiegeld; over statiegeld belasten we niets door. */
  omzetCent: Cent;
}

export interface TransactiekostenResultaat {
  perBonCent: Cent;
  overOmzetCent: Cent;
  totaalCent: Cent;
}

export function berekenTransactiekosten(
  model: Prijsmodel,
  invoer: TransactiekostenInvoer,
): TransactiekostenResultaat {
  const perBonCent = tariefMaalAantal(model.perBonTiendeCent, invoer.aantalBonnen);
  const overOmzetCent = percentageVan(Math.max(0, invoer.omzetCent), model.overOmzetBasispunten);
  return { perBonCent, overOmzetCent, totaalCent: perBonCent + overOmzetCent };
}

/**
 * Wat één kraam aan doorbelaste transactiekosten krijgt: het vaste bedrag per
 * bon (in tienden van een cent) plus het percentage over de omzet, samen in
 * hele centen. Het dagtarief hoort er niet bij: dat betaalt de organisator.
 *
 * Eén plek voor deze som. Het dashboard en de serverfunctie die de facturen
 * vaststelt deden hem elk zelf, zonder door tien te delen, en kwamen daardoor
 * op tien keer te veel uit (gevonden op 21 september 2026, nog vóór een echt
 * evenement).
 *
 * `omzetCent` is de omzet exclusief statiegeld (regel 10).
 */
export function berekenKraamTransactiekosten(
  tarieven: Pick<Prijsmodel, 'perBonTiendeCent' | 'overOmzetBasispunten'>,
  invoer: TransactiekostenInvoer,
): Cent {
  return berekenTransactiekosten({ ...LEEG_PRIJSMODEL, ...tarieven }, invoer).totaalCent;
}

/** Wat de organisator betaalt voor de kraamsets: sets maal dagen maal tarief. */
export function berekenKraamsetkosten(
  model: Prijsmodel,
  aantalKraamsets: number,
  aantalDagen: number,
): Cent {
  return vermenigvuldig(
    vermenigvuldig(model.dagtariefPerKraamsetCent, aantalKraamsets),
    aantalDagen,
  );
}
